<script setup lang="ts">
import { CheckCircleIcon, PencilIcon, TrashIcon, XCircleIcon } from '@heroicons/vue/24/outline';
import { useI18n } from 'vue-i18n';
import type { MenuItem } from '@/types/menu';
import DropdownMenu from '@/components/ui/DropdownMenu.vue';
import DropdownMenuDivider from '@/components/ui/DropdownMenuDivider.vue';
import DropdownMenuItem from '@/components/ui/DropdownMenuItem.vue';

interface Props {
  item: MenuItem;
  dropdownId: string;
  canManage: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  edit: [item: MenuItem];
  toggleAvailability: [item: MenuItem];
  delete: [item: MenuItem];
}>();

const { t } = useI18n();

function isItemAvailable(item: MenuItem): boolean {
  return item.isAvailable ?? item.is_available ?? true;
}

function handleEdit() {
  emit('edit', props.item);
}

function handleToggleAvailability() {
  emit('toggleAvailability', props.item);
}

function handleDelete() {
  emit('delete', props.item);
}
</script>

<template>
  <DropdownMenu
    v-if="canManage && item.id"
    :id="dropdownId"
    button-label="Menu item actions"
    width="md"
  >
    <DropdownMenuItem
      :icon="PencilIcon"
      variant="default"
      @click="handleEdit"
    >
      {{ t('app.views.menu.list.edit') }}
    </DropdownMenuItem>

    <DropdownMenuItem
      :icon="isItemAvailable(item) ? XCircleIcon : CheckCircleIcon"
      :variant="isItemAvailable(item) ? 'warning' : 'success'"
      @click="handleToggleAvailability"
    >
      {{ isItemAvailable(item) ? t('app.views.menu.list.disable') : t('app.views.menu.list.enable') }}
    </DropdownMenuItem>

    <DropdownMenuDivider />

    <DropdownMenuItem
      :icon="TrashIcon"
      variant="danger"
      @click="handleDelete"
    >
      {{ t('app.views.menu.list.delete') }}
    </DropdownMenuItem>
  </DropdownMenu>
</template>
